/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { css } from "@emotion/css";
import { Callout } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArtifactResource, GitRefResource, IHaveSnapshotGitReference, TaskDetailsResource, TaskResource } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, TaskName } from "@octopusdeploy/octopus-server-client";
import { useAnalyticSession } from "@octopusdeploy/portal-analytics";
import { links, usePageEventDispatcher } from "@octopusdeploy/portal-routes";
import type * as React from "react";
import type { Location, NavigateFunction } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { createProjectIdField, createResourceTypeField, createStringField } from "~/analytics/fields";
import { GitRefLink } from "~/areas/projects/components/Releases/GitRefLink/GitRefLink";
import { getCommitLink } from "~/areas/projects/components/Releases/gitHostLinkGenerator";
import { useOptionalProjectContext } from "~/areas/projects/context";
import QueuedTaskDetails from "~/areas/tasks/components/Task/QueuedTaskDetails";
import { client } from "~/clientInstance";
import type { DataBaseComponentState, Refresh } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import InternalLink from "~/components/Navigation/InternalLink/index";
import DateFormatter from "~/utils/DateFormatter";
import { Section } from "../../../../../components/Section/Section";
import type { UniqueActivityElement } from "../../../../../components/TaskLogLines/TaskLogBlock";
import { goToLogElement } from "../TaskLog/TaskLogUtil";
import TaskSummaryLine from "../TaskSummary/TaskSummaryLine";
interface TaskSummaryComponentProps {
    task: TaskResource<any>;
    projectId?: string;
    environmentId?: string;
    tenantId?: string;
    artifacts?: ArtifactResource[];
    activityElements?: UniqueActivityElement[];
    taskDetails?: TaskDetailsResource;
    doRefresh: Refresh;
    snapshot: IHaveSnapshotGitReference;
    stepsCorrelationIds?: {
        [key: string]: string;
    };
    gitRef?: GitRefResource;
    deploymentStatus?: string;
}
type TaskSummaryProps = TaskSummaryComponentProps;
type TaskSummaryInternalProps = TaskSummaryComponentProps & {
    navigate: NavigateFunction;
    location: Location;
} & {
    dispatcher: ReturnType<typeof usePageEventDispatcher>;
    anonymizedProjectId?: string;
};
const ProcessSnapshotCell: React.FC<{
    resource: IHaveSnapshotGitReference;
}> = ({ resource }) => {
    const projectContext = useOptionalProjectContext();
    if (!projectContext?.state.model) {
        return null;
    }
    const project = projectContext.state.model;
    if (!(project.IsVersionControlled && resource && resource.VersionControlReference)) {
        return null;
    }
    const commitUrl = HasGitPersistenceSettings(project.PersistenceSettings) ? getCommitLink(project.PersistenceSettings.Url, resource.VersionControlReference.GitCommit) : null;
    return <GitRefLink gitRef={resource.VersionControlReference} commitUrl={commitUrl}/>;
};
ProcessSnapshotCell.displayName = "ProcessSnapshotCell"
const TaskInfoTable: React.FC<{
    task: TaskResource;
    snapshot: IHaveSnapshotGitReference;
}> = ({ task, snapshot }) => {
    const startTime = task.StartTime ? DateFormatter.dateToShortFormat(task.StartTime) : "Not started";
    const endTime = task.CompletedTime ? DateFormatter.dateToShortFormat(task.CompletedTime) : "N/A";
    const processSnapshotContent = <ProcessSnapshotCell resource={snapshot}/>;
    const hasProcessSnapshot = snapshot && snapshot.VersionControlReference;
    return (<div className={taskInfoGrid}>
            <div className={gridHeader}>Ran on</div>
            <div className={gridHeader}>Start</div>
            <div className={gridHeader}>End</div>
            <div className={gridHeader}>Duration</div>
            {hasProcessSnapshot && <div className={gridHeader}>Process Snapshot</div>}

            <div className={gridCell}>{task.ServerNode ?? "N/A"}</div>
            <div className={gridCell}>{startTime}</div>
            <div className={gridCell}>{endTime}</div>
            <div className={gridCell}>{task.Duration || "N/A"}</div>
            {hasProcessSnapshot && <div className={gridCell}>{processSnapshotContent}</div>}
        </div>);
};
TaskInfoTable.displayName = "TaskInfoTable"
class TaskSummaryInternal extends DataBaseComponent<TaskSummaryInternalProps, DataBaseComponentState> {
    componentDidMount() {
        this.props.dispatcher("View Task Summary", {
            ...createResourceTypeField("Task Summary"),
            ...createStringField("Deployment Status", this.props.deploymentStatus),
            ...createProjectIdField(this.props.anonymizedProjectId),
        });
    }
    render() {
        const { task, taskDetails } = this.props;
        const showFatalsInline = this.props.activityElements!.length !== 1;
        const taskIsWaitingToStart = !(task.HasBeenPickedUpByProcessor || task.HasPendingInterruptions);
        return (<>
                <TaskInfoTable task={task} snapshot={this.props.snapshot}/>
                <Section className={summaryTopLevel}>
                    {task.Name === TaskName.BulkDeployment && client.spaceId && task.ProjectId ? (<Callout hideTitle={true} type={"information"}>
                            <InternalLink to={links.projectTasksPage.generateUrl({ spaceId: client.spaceId, projectSlug: task.ProjectId }, { batch: task.Arguments.BulkDeploymentId })}>View all deployments </InternalLink>
                            created by this task.
                        </Callout>) : null}
                    {this.props.activityElements!.map((e) => (<TaskSummaryLine key={e.uniqueId} element={e} artifacts={this.props.artifacts!} onLogNavigation={(elementId) => goToLogElement(this.props, elementId)} showFatalsInline={showFatalsInline} stepsCorrelationIds={this.props.stepsCorrelationIds} gitRef={this.props.gitRef}/>))}
                    {taskIsWaitingToStart && taskDetails ? <QueuedTaskDetails task={task} taskDetails={taskDetails} doBusyTask={this.doBusyTask}/> : null}
                </Section>
            </>);
    }
    static displayName = "TaskSummaryInternal";
}
const taskInfoGrid = css({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(0, max-content))",
    border: "1px solid",
    borderColor: themeTokens.color.border.primary,
    borderRadius: borderRadius.small,
    margin: `${space[16]} ${space[16]} 0 ${space[16]}`,
    font: text.body.regular.small,
});
const gridHeader = css({
    fontWeight: "bold",
    textAlign: "left",
    paddingTop: space[12],
    paddingLeft: space[16],
    paddingRight: space[16],
    paddingBottom: space[4],
    gridRow: 1,
});
const gridCell = css({
    wordBreak: "break-all",
    paddingBottom: space[12],
    paddingLeft: space[16],
    paddingRight: space[16],
    gridRow: 2,
});
const summaryTopLevel = css({
    paddingTop: "1rem",
});
export default function TaskSummary(props: TaskSummaryProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatcher = usePageEventDispatcher();
    const session = useAnalyticSession();
    const anonymizedProjectId = props.projectId === undefined ? undefined : session.anonymize(props.projectId);
    return <TaskSummaryInternal dispatcher={dispatcher} navigate={navigate} location={location} {...props} anonymizedProjectId={anonymizedProjectId}/>;
}
